import React from 'react'

import { Banner, BaseContent, Button, Subsection } from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import type { Credential, Device, IdentityProvider } from './Security.types'
import apple from './assets/Apple.svg'
import discord from './assets/Discord.svg'
import facebook from './assets/Facebook.svg'
import google from './assets/Google.svg'
import instagram from './assets/Instagram.svg'
import meta from './assets/Meta.svg'
import streamelements from './assets/StreamElements.svg'
import streamlabs from './assets/Streamlabs.svg'
import tiktok from './assets/TikTok.svg'
import twitch from './assets/Twitch.svg'
import x from './assets/X.svg'
import youtube from './assets/YouTube.svg'
import { CredentialList } from './components/CredentialList/CredentialList'
import { DeviceList } from './components/DeviceList/DeviceList'
import { LoginPlatform } from './components/LoginPlatform/LoginPlatform'
import { IDENTITY_PROVIDERS } from './consts'

export const CONNECTION_ICONS = {
  apple,
  discord,
  facebook,
  google,
  instagram,
  meta,
  streamelements,
  streamlabs,
  tiktok,
  twitch,
  x,
  youtube,
}

export const CONNECTION_ICONS_MAP: Record<IdentityProvider, keyof typeof CONNECTION_ICONS> = {
  'twitter-v2': 'x',
  apple: 'apple',
  facebook: 'meta',
  google: 'google',
  twitch: 'twitch',
  youtube: 'youtube',
}

export type SecurityViewProps = {
  password: {
    createdDate: string | null
    updatePasswordUrl: string
  } | null
  loginPlatforms: {
    items: { provider: IdentityProvider; username: string }[]
  } | null
  passkeys: {
    credentials: Credential[]
    setUpUrl: string
    onDelete: (id: string) => void
  } | null
  authenticators: {
    credentials: Credential[]
    setUpUrl: string
    onDelete: (id: string) => void
  } | null
  devices: {
    items: Device[] & {
      onDelete?: () => void
    }
  } | null
}

export const SecurityView = ({
  password,
  loginPlatforms,
  passkeys,
  authenticators,
  devices,
}: SecurityViewProps) => {
  const renderBanner = () => {
    if (!passkeys || !authenticators) return null

    if (passkeys.credentials.length + authenticators.credentials.length > 1) return null

    return (
      <Subsection>
        <Banner>
          <p>
            We recommend adding more than one 2FA method to your account. This ensures that you can
            still sign in to your account, even if you lose one of your methods.
          </p>
        </Banner>
      </Subsection>
    )
  }

  return (
    <>
      {renderBanner()}
      {password && (
        <Subsection title="Password">
          <BaseContent>
            {password.createdDate ? (
              <p>
                You’ve changed your password on{' '}
                <TimezonedDateString date={password.createdDate} format="MMM D, YYYY" />.
                <br />
                <a href={password.updatePasswordUrl}>Change password</a>
              </p>
            ) : (
              <p>
                You’ve never set your password.
                <br />
                <a href={password.updatePasswordUrl}>Set password</a>
              </p>
            )}
          </BaseContent>
        </Subsection>
      )}
      {loginPlatforms && (
        <Subsection title="Login platforms">
          <div style={{ display: 'grid', gap: 12 }}>
            <BaseContent>
              <p>External login platforms.</p>
              {/* <p>Manage your connections to external login platforms.</p> */}
            </BaseContent>
            {loginPlatforms.items.map((item) => (
              <LoginPlatform
                image={CONNECTION_ICONS[CONNECTION_ICONS_MAP[item.provider]]}
                provider={IDENTITY_PROVIDERS[item.provider]}
                username={item.username}
              />
            ))}
          </div>
        </Subsection>
      )}
      {passkeys && (
        <Subsection title="Passkey (recommended)">
          <BaseContent>
            {/* TODO: Learn more */}
            <p>Use your passkey (e.g., face or fingerprint scan) to sign in.</p>
          </BaseContent>
          {!!passkeys.credentials.length && (
            <div style={{ marginTop: 12 }}>
              <CredentialList
                items={passkeys.credentials.map((credential) => ({
                  ...credential,
                  description: (
                    <TimezonedDateString
                      date={credential.createdDate}
                      format="[Created] MMM DD, YYYY [at] h:mm A"
                    />
                  ),
                }))}
                onDelete={passkeys.onDelete}
              />
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex' }}>
            {passkeys.credentials.length === 0 ? (
              <Button label="Set up passkey" href={passkeys.setUpUrl} />
            ) : (
              <BaseContent>
                <a href={passkeys.setUpUrl}>Set up another passkey</a>
              </BaseContent>
            )}
          </div>
        </Subsection>
      )}
      {authenticators && (
        <Subsection title="Two-factor authenticator application">
          <BaseContent>
            {/* TODO: Learn more */}
            <p>Enter a code from authenticator application to sign in.</p>
          </BaseContent>
          {!!authenticators.credentials.length && (
            <div style={{ marginTop: 12 }}>
              <CredentialList
                items={authenticators.credentials.map((credential) => ({
                  ...credential,
                  description: (
                    <TimezonedDateString
                      date={credential.createdDate}
                      format="[Created] MMM DD, YYYY [at] h:mm A"
                    />
                  ),
                }))}
                onDelete={authenticators.onDelete}
              />
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex' }}>
            {authenticators.credentials.length === 0 ? (
              <Button label="Set up authenticator application" href={authenticators.setUpUrl} />
            ) : (
              <BaseContent>
                <a href={authenticators.setUpUrl}>Set up another authenticator application</a>
              </BaseContent>
            )}
          </div>
        </Subsection>
      )}
      {devices && (
        <Subsection title="Logged-in devices">
          <DeviceList items={devices.items} />
        </Subsection>
      )}
    </>
  )
}
